import { isApiError } from '../../api';

export const SERVER_DOWN_MESSAGE =
  'O servidor está indisponível no momento. Tente de novo em instantes.';

export const BAD_REQUEST_MESSAGE = 'Confira os dados informados e tente de novo.';

export const FORBIDDEN_MESSAGE =
  'Você não tem permissão para fazer isso. Fale com outro administrador.';

/** `last_admin` (409): o servidor nunca deixa a caixa de entrada sem administrador ativo. */
export const LAST_ADMIN_MESSAGE =
  'Deve existir ao menos um administrador ativo. Promova outra pessoa a administrador antes de desativar ou rebaixar esta.';

/** `vault_not_configured` (503): quem consegue consertar é quem administra o SERVIDOR, não esta tela. */
export const VAULT_NOT_CONFIGURED_MESSAGE =
  'O servidor ainda não está preparado para guardar as credenciais da conexão: falta a chave de criptografia (CREDENTIALS_KEY). Peça a quem administra o servidor para definir essa variável de ambiente e reiniciá-lo; depois, salve de novo.';

/** `invalid_credentials` (400): o WhatsApp (o serviço por trás da conexão) recusou o que foi informado. */
export const INVALID_CREDENTIALS_MESSAGE =
  'As credenciais foram recusadas pelo serviço do WhatsApp. Confira os dados informados e tente de novo.';

/** `provider_unavailable` (502): o serviço por trás da conexão está fora do ar (o nosso servidor está bem). */
export const PROVIDER_DOWN_MESSAGE =
  'Não foi possível falar com o serviço do WhatsApp agora. Tente de novo em instantes.';

/** `not_found` nas rotas de demonstração: a instalação não é de demonstração. */
export const NOT_DEMO_MESSAGE = 'Esta instalação não é de demonstração.';

/**
 * Mensagem para a pessoa quando uma chamada de administração falha.
 *
 * Os códigos de domínio do servidor (`email_taken`, `invalid_email`, `weak_password`,
 * `password_required`…) já chegam com texto em português e são mostrados como vieram. Fora deles o
 * texto cru não presta: `bad_request` traz a frase em inglês do validador, e os 5xx, "Erro
 * interno". Erros de rede, tempo esgotado e sessão (`invalid_csrf`) já vêm em português do
 * cliente HTTP. O que não é `ApiError` usa `fallback`.
 */
export function adminErrorMessage(error: unknown, fallback: string): string {
  if (!isApiError(error)) return fallback;
  if (error.code === 'invalid_csrf') return error.message;
  if (error.code === 'last_admin') return LAST_ADMIN_MESSAGE;
  if (error.code === 'vault_not_configured') return VAULT_NOT_CONFIGURED_MESSAGE;
  if (error.code === 'invalid_credentials') return INVALID_CREDENTIALS_MESSAGE;
  // Antes da regra dos 5xx: um 502 do serviço do WhatsApp não é o NOSSO servidor fora do ar.
  if (error.code === 'provider_unavailable') return PROVIDER_DOWN_MESSAGE;
  if (error.code === 'bad_request') return BAD_REQUEST_MESSAGE;
  if (error.status >= 500) return SERVER_DOWN_MESSAGE;
  if (error.status === 403) return FORBIDDEN_MESSAGE;
  return error.message;
}

/**
 * Mensagem para quando salvar a conexão falha. É a `adminErrorMessage`, com duas exceções em que o
 * servidor já responde em português dizendo O QUE conferir, e isso ajuda mais do que o texto
 * genérico: `bad_request` (QUAL campo falta ou é inválido), `invalid_credentials` (o motivo da
 * recusa: token vencido, sem permissão, ID da conta errado…) e `provider_error` (o serviço falhou e a
 * mensagem diz o que fazer, ex.: o registro do número pode ter sido concluído).
 */
export function connectionErrorMessage(error: unknown, fallback: string): string {
  if (
    isApiError(error) &&
    (error.code === 'bad_request' ||
      error.code === 'invalid_credentials' ||
      error.code === 'provider_error') &&
    error.message.trim() !== ''
  ) {
    return error.message;
  }
  return adminErrorMessage(error, fallback);
}

/** Campos do formulário de usuário a que cada erro do servidor se refere (os demais são do formulário). */
export function userErrorField(error: unknown): 'name' | 'email' | 'password' | null {
  if (!isApiError(error)) return null;
  switch (error.code) {
    case 'invalid_name':
      return 'name';
    case 'invalid_email':
    case 'email_taken':
      return 'email';
    case 'weak_password':
      return 'password';
    default:
      return null;
  }
}
